/**
 * SoleadLab 2.0 — Share-Link Client (F-02D public + F-02E-1 owner CRUD)
 *
 * Frontend wrappers for the backend share endpoints.
 *
 * Public (F-02C-1, no auth header — cookie-based):
 *   GET  /api/v2/share/:token         → resolveShareToken
 *   POST /api/v2/share/:token/unlock  → unlockShareToken
 *
 * Authenticated owner/operator CRUD (F-02B, Bearer + X-Org-Id via apiFetch):
 *   GET    /api/v2/sites/:siteId/share-links     → listShareLinks
 *   POST   /api/v2/sites/:siteId/share-links     → createShareLink
 *   PATCH  /api/v2/share-links/:id               → updateShareLink
 *   POST   /api/v2/share-links/:id/revoke        → revokeShareLink
 *   DELETE /api/v2/share-links/:id               → deleteShareLink
 *
 * The public helpers use raw fetch with `credentials: 'include'` so the
 * backend Set-Cookie sl_share lands in the browser jar. The owner CRUD
 * uses the shared apiFetch (Bearer + X-Org-Id + 401-refresh).
 *
 * Field naming policy: we preserve the backend's snake_case shape verbatim
 * (matches `services/mobileUploadApi.ts:11-22`). No client-side camelCase
 * remap on responses. Create/update *request* payloads use camelCase to
 * match the zod schemas in soleadlab-api/src/schemas/shareLinks.ts.
 */

import { API_BASE, ApiError, apiFetch } from './apiFetch';
import type { ShareView } from '../stores/shareSessionStore';

/**
 * Re-export the canonical view shape so callers can `import { ShareView }`
 * from either `shareApi` or `shareSessionStore` — both point at the same
 * definition.
 */
export type { ShareView };

/** Backend response envelope (matches utils/response.ts:success). */
interface SuccessEnvelope<T> {
  success: true;
  data: T;
  meta: { timestamp: string; version: string };
}

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string; details?: unknown };
  meta: { timestamp: string; version: string };
}

async function readEnvelope<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('Content-Type') ?? '';
  const body = contentType.includes('application/json')
    ? ((await response.json().catch(() => null)) as SuccessEnvelope<T> | ErrorEnvelope | null)
    : null;

  if (!response.ok) {
    const err = body && 'error' in body ? body.error : undefined;
    throw new ApiError(
      err?.message || `HTTP ${response.status}`,
      response.status,
      err?.code || 'UNKNOWN',
      err?.details,
    );
  }

  if (body && 'data' in body) return body.data;
  // Defensive: backend always wraps in { data }, but if a future endpoint
  // returns bare JSON, accept it rather than crashing.
  return body as unknown as T;
}

/**
 * Resolve a raw share token. Triggers backend cookie issuance for
 * no-passphrase links; passphrase-protected links return metadata only.
 *
 * Throws `ApiError` with status:
 *   404 NOT_FOUND        → unknown token
 *   403 SHARE_LINK_REVOKED → revoked
 *   403 SHARE_LINK_EXPIRED → expired
 *   500 INTERNAL_ERROR     → backend hiccup
 */
export async function resolveShareToken(token: string): Promise<ShareView> {
  const response = await fetch(`${API_BASE}/api/v2/share/${encodeURIComponent(token)}`, {
    method: 'GET',
    credentials: 'include',
  });
  return readEnvelope<ShareView>(response);
}

/**
 * Submit a passphrase for a passphrase-protected share link. On success,
 * the backend sets the `sl_share` cookie with `unlocked: true`.
 *
 * Throws `ApiError` with status:
 *   401 INVALID_PASSPHRASE   → wrong passphrase
 *   400 BAD_REQUEST          → link does not require a passphrase
 *   404 NOT_FOUND            → unknown token
 *   403 SHARE_LINK_REVOKED   → revoked
 *   403 SHARE_LINK_EXPIRED   → expired
 *   429 RATE_LIMITED         → 5+ attempts/minute from this IP/token
 *   422 VALIDATION_ERROR     → bad payload shape
 */
export async function unlockShareToken(
  token: string,
  passphrase: string,
): Promise<ShareView> {
  const response = await fetch(
    `${API_BASE}/api/v2/share/${encodeURIComponent(token)}/unlock`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ passphrase }),
    },
  );
  return readEnvelope<ShareView>(response);
}

// ─── F-02E-1: Authenticated owner/operator CRUD ─────────────────────────────

/**
 * Allowed opt-in sections; mirrors `ALLOWED_EXPOSED_SECTIONS` in
 * soleadlab-api/src/schemas/shareLinks.ts. Owners can author any subset.
 * `core` is implicit (always included) and is NOT a toggle. `copilot` is
 * intentionally excluded — deferred per F-02 product decisions.
 */
export const SHARE_LINK_EXPOSED_SECTION_OPTIONS = [
  'tasks',
  'iv',
  'scores',
  'state',
  'monitoring',
  // R3-D share exposure (owner, 2026-09-27): the digital layout — module
  // grid, string lines and labels — is an opt-in per link. Plan vectors
  // (CAD/PDF linework) are NOT a section: they show whenever the operator
  // picked layers.
  'layout',
] as const;

export type ShareLinkExposedSection =
  (typeof SHARE_LINK_EXPOSED_SECTION_OPTIONS)[number];

/**
 * Human labels for the opt-in sections. Shared by the create modal and the
 * manager panel's in-place sections editor (F-02E-5) so both surfaces name
 * the same section the same way.
 */
export const SHARE_LINK_SECTION_LABELS: Record<ShareLinkExposedSection, string> = {
  tasks: 'Tasks',
  // F-02E-5b: 'iv' exposes the IV Results map layer (legend + polygons),
  // not the IV Reporting ticket.
  iv: 'IV results on map',
  scores: 'Site scores',
  state: 'Asset state',
  monitoring: 'Monitoring',
  layout: 'Digital layout (module grid & labels)',
};

export const SHARE_LINK_DEFAULT_EXPIRY_DAYS = 30;
export const SHARE_LINK_MAX_EXPIRY_DAYS = 90;

/**
 * F-02E-3B/3C: the far-future sentinel the backend writes into the NOT NULL
 * `expires_at` column for never-expiring links (the backend gates on the
 * `never_expires` flag, but the public visitor response only carries
 * `expires_at`). Must match `NEVER_EXPIRES_SENTINEL_ISO` in
 * soleadlab-api/src/handlers/shareLinks.ts.
 */
export const NEVER_EXPIRES_SENTINEL_ISO = '9999-12-31T23:59:59.000Z';

/**
 * True iff the given ISO string is the never-expire sentinel. Conservative:
 * exact match only — a real far-future finite expiry is NOT classified as
 * never-expire. Missing/invalid input returns false.
 */
export function isNeverExpireSentinel(iso?: string | null): boolean {
  return iso === NEVER_EXPIRES_SENTINEL_ISO;
}

/**
 * Owner-visible projection returned by GET list / PATCH / revoke endpoints.
 * Matches `ShareLinkView` in soleadlab-api/src/handlers/shareLinks.ts.
 * Notably DOES NOT include `token`, `token_hash`, or `passphrase_hash`.
 */
export interface ShareLinkSummary {
  id: string;
  site_id: string;
  label: string | null;
  expires_at: string;
  status: 'active' | 'revoked';
  derived_status: 'active' | 'revoked' | 'expired';
  exposed_sections: string[];
  has_passphrase: boolean;
  view_count: number;
  last_viewed_at: string | null;
  created_at: string;
  created_by: string;
  revoked_at: string | null;
  revoked_by: string | null;
  token_prefix: string;
  /** F-02E-3B: true when the link never expires (expires_at holds a sentinel). */
  never_expires: boolean;
  /** F-02E-3B: true when an active link has a recoverable encrypted token (copy-url works). */
  copy_available: boolean;
  /** F-02E-3B: true when an active link is a legacy row with no stored ciphertext — regenerate to make copyable. */
  requires_regenerate: boolean;
  /** FL-1: 'view' = read-only; 'field' = indication write surface for technicians. */
  kind: 'view' | 'field';
}

/**
 * Create-response shape (POST). Includes the raw `token` and a
 * routable `share_path` — these appear ONLY in this response and are
 * never re-fetchable. Callers must surface the URL to the user
 * immediately and discard. The regenerate endpoint returns the same shape.
 */
export interface ShareLinkCreated {
  id: string;
  token: string;
  share_path: string;
  site_id: string;
  label: string | null;
  expires_at: string;
  status: 'active';
  exposed_sections?: string[];
  has_passphrase: boolean;
  created_at: string;
  created_by: string;
  token_prefix: string;
  /** F-02E-3B: true when the link never expires. */
  never_expires: boolean;
  /** FL-1: link kind as created. */
  kind: 'view' | 'field';
}

/**
 * F-02E-3B: response of the owner copy-url endpoint. Carries the recovered
 * routable paths (built from the decrypted raw token) — never `token_hash`
 * or the ciphertext envelope.
 */
export interface ShareLinkCopyUrl {
  id: string;
  share_path: string;
  viewer_path: string;
  never_expires: boolean;
  expires_at: string;
}

/** POST body for createShareLink (matches CreateShareLinkSchema). */
export interface CreateShareLinkPayload {
  label?: string | null;
  expiresAt?: string;
  passphrase?: string | null;
  exposedSections?: ShareLinkExposedSection[];
  /** F-02E-3B: opt-in never-expire. Mutually exclusive with `expiresAt`. */
  neverExpires?: boolean;
  /**
   * FL-1: omit or 'view' for read-only (default); 'field' mints a
   * write-capable technician link. Immutable after creation; the backend
   * rejects `neverExpires` for field links.
   */
  kind?: 'view' | 'field';
}

/** PATCH body for updateShareLink (matches UpdateShareLinkSchema). */
export interface UpdateShareLinkPayload {
  label?: string | null;
  expiresAt?: string;
  passphrase?: string | null;
  exposedSections?: ShareLinkExposedSection[];
  /** F-02E-3B: toggle never-expire. `true` is mutually exclusive with `expiresAt`. */
  neverExpires?: boolean;
}

/**
 * List all share links for a site (most recent first). Backend filters
 * by tenant access via `assertSiteAccess`, so this is safe to call as
 * any role; it returns 403 if the caller lacks site access.
 */
export async function listShareLinks(
  siteId: string,
): Promise<ShareLinkSummary[]> {
  return apiFetch<ShareLinkSummary[]>(
    `/api/v2/sites/${encodeURIComponent(siteId)}/share-links`,
  );
}

/**
 * Create a new share link. The response contains the raw token and a
 * relative `share_path` like `/share/<token>`. Build the absolute URL
 * with `buildShareUrlFromPath(share_path)` for the copy-to-clipboard
 * UX. The raw token is never returned again from any other endpoint.
 */
export async function createShareLink(
  siteId: string,
  payload: CreateShareLinkPayload,
): Promise<ShareLinkCreated> {
  return apiFetch<ShareLinkCreated>(
    `/api/v2/sites/${encodeURIComponent(siteId)}/share-links`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

/**
 * Update an existing (active) share link. Rejected with 409 if the link
 * is already revoked (revoked is terminal in Phase 1 — owners mint a new
 * link instead).
 */
export async function updateShareLink(
  id: string,
  payload: UpdateShareLinkPayload,
): Promise<ShareLinkSummary> {
  return apiFetch<ShareLinkSummary>(
    `/api/v2/share-links/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

/**
 * Revoke a share link. Idempotent: re-revoking returns 200 with the
 * current state. After revoke, the link's `status` becomes `revoked`
 * and any sl_share cookie minted from it is rejected on next request.
 */
export async function revokeShareLink(
  id: string,
): Promise<ShareLinkSummary> {
  return apiFetch<ShareLinkSummary>(
    `/api/v2/share-links/${encodeURIComponent(id)}/revoke`,
    { method: 'POST' },
  );
}

/**
 * Permanently delete a share link row. Audit row is written by the
 * backend BEFORE the DELETE so the deletion is traceable. Returns
 * `{ id, deleted: true }` on success.
 */
export async function deleteShareLink(
  id: string,
): Promise<{ id: string; deleted: true }> {
  return apiFetch<{ id: string; deleted: true }>(
    `/api/v2/share-links/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

/**
 * F-02E-3B: recover the copyable share URL for an active link by asking the
 * backend to decrypt its at-rest token. Use `buildShareUrlFromPath(share_path)`
 * for the clipboard. The raw token never reaches the client except via this
 * response and the original create/regenerate responses.
 *
 * Throws `ApiError` with status:
 *   403 SHARE_LINK_REVOKED            → link was revoked
 *   403 SHARE_LINK_EXPIRED            → finite link already expired
 *   409 SHARE_LINK_TOKEN_UNAVAILABLE  → legacy row (no stored ciphertext) or
 *                                        unrecoverable token → regenerate to copy
 */
export async function copyShareLinkUrl(id: string): Promise<ShareLinkCopyUrl> {
  return apiFetch<ShareLinkCopyUrl>(
    `/api/v2/share-links/${encodeURIComponent(id)}/copy-url`,
  );
}

/**
 * F-02E-3B: re-mint the raw token for an active link. Rotates `token_hash` +
 * `token_ciphertext` server-side, so the PREVIOUS URL stops resolving
 * immediately (becomes 404). Returns the same shape as create — surface the
 * new URL once with a copy action.
 *
 * Throws `ApiError` with status:
 *   409 (CONFLICT)        → link is revoked (mint a new link instead)
 *   409 SHARE_LINK_EXPIRED → finite link already expired (extend expiry first)
 */
export async function regenerateShareLink(id: string): Promise<ShareLinkCreated> {
  return apiFetch<ShareLinkCreated>(
    `/api/v2/share-links/${encodeURIComponent(id)}/regenerate`,
    { method: 'POST' },
  );
}

/**
 * Build a fully-qualified visitor URL from the backend-returned
 * `share_path` (`/share/<token>`). The backend deliberately returns
 * a relative path so it doesn't have to know the host; we assemble
 * it client-side from `window.location.origin`. Falls back to
 * returning the raw path if `window` is not available (SSR / tests
 * with a stubbed environment).
 */
export function buildShareUrlFromPath(sharePath: string): string {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return sharePath;
  }
  // Ensure no double-slash if backend ever changes to absolute URL.
  if (/^https?:\/\//.test(sharePath)) return sharePath;
  return `${window.location.origin}${sharePath}`;
}
